import {
  ClubApi,
  type ClubItemType,
  type EventRsvpStatus,
  type EventSeriesInput,
} from "@/api/club/ClubApi";
import { queryKeys } from "@/api/queryKeys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

function invalidateClubOrg(qc: ReturnType<typeof useQueryClient>, orgId?: string) {
  if (orgId) {
    void qc.invalidateQueries({ queryKey: queryKeys.club.feed(orgId) });
    void qc.invalidateQueries({ queryKey: ["club", "events", orgId] });
    void qc.invalidateQueries({ queryKey: ["club", "search", orgId] });
  } else {
    void qc.invalidateQueries({ queryKey: ["club"] });
  }
}

function invalidateItem(qc: ReturnType<typeof useQueryClient>, itemId?: string) {
  if (itemId) {
    void qc.invalidateQueries({ queryKey: queryKeys.club.item(itemId) });
    void qc.invalidateQueries({ queryKey: queryKeys.club.comments(itemId) });
  }
}

export function useClubFeed(organizationId: string | undefined, enabled = true) {
  const q = useQuery({
    queryKey: queryKeys.club.feed(organizationId ?? ""),
    queryFn: () => ClubApi.listClubFeed({ organizationId: organizationId! }),
    enabled: !!organizationId && enabled,
    staleTime: 15_000,
  });
  return {
    items: q.data ?? [],
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useClubItem(itemId: string | undefined, enabled = true) {
  const q = useQuery({
    queryKey: queryKeys.club.item(itemId ?? ""),
    queryFn: () => ClubApi.getClubItem(itemId!),
    enabled: !!itemId && enabled,
    staleTime: 10_000,
  });
  return {
    item: q.data ?? null,
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useClubEventsBetween(
  organizationId: string | undefined,
  from: string | undefined,
  to: string | undefined,
  enabled = true
) {
  const q = useQuery({
    queryKey: queryKeys.club.events(organizationId ?? "", from ?? "", to ?? ""),
    queryFn: () =>
      ClubApi.listEventsBetween({
        organizationId: organizationId!,
        from: from!,
        to: to!,
      }),
    enabled: !!organizationId && !!from && !!to && enabled,
    staleTime: 20_000,
  });
  return {
    events: q.data ?? [],
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useClubComments(itemId: string | undefined, enabled = true) {
  const q = useQuery({
    queryKey: queryKeys.club.comments(itemId ?? ""),
    queryFn: () => ClubApi.listClubComments(itemId!),
    enabled: !!itemId && enabled,
    staleTime: 10_000,
  });
  return {
    comments: q.data ?? [],
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useClubSearch(
  organizationId: string | undefined,
  query: string,
  types?: ClubItemType[] | null,
  enabled = true
) {
  const trimmed = query.trim();
  const q = useQuery({
    queryKey: queryKeys.club.search(
      organizationId ?? "",
      trimmed,
      (types ?? []).join(",")
    ),
    queryFn: () =>
      ClubApi.searchClub({
        organizationId: organizationId!,
        query: trimmed,
        types: types ?? null,
      }),
    enabled: !!organizationId && trimmed.length >= 2 && enabled,
    staleTime: 15_000,
  });
  return {
    results: q.data ?? [],
    loading: q.isLoading,
    error: q.error,
  };
}

export function useCaseChips(caseIds: string[], enabled = true) {
  const key = [...caseIds].sort().join(",");
  const q = useQuery({
    queryKey: queryKeys.club.caseChips(key),
    queryFn: () => ClubApi.resolveCaseChips(caseIds),
    enabled: caseIds.length > 0 && enabled,
    staleTime: 60_000,
  });
  return {
    chips: q.data ?? [],
    byId: Object.fromEntries((q.data ?? []).map((c) => [c.case_id, c])),
    loading: q.isLoading,
  };
}

export function useMemberMiniProfile(profileId: string | undefined, enabled = true) {
  const q = useQuery({
    queryKey: queryKeys.club.memberMini(profileId ?? ""),
    queryFn: () => ClubApi.getMemberMiniProfile(profileId!),
    enabled: !!profileId && enabled,
    staleTime: 60_000,
  });
  return { profile: q.data ?? null, loading: q.isLoading };
}

// ── Mutations ─────────────────────────────────────────────────────────

export function useCreateClubPost(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title?: string | null;
      body: string;
      isOfficial?: boolean;
      mentionedProfileIds?: string[];
    }) =>
      ClubApi.createClubPost({
        organizationId: organizationId!,
        ...input,
      }),
    onSuccess: () => invalidateClubOrg(qc, organizationId),
  });
}

export function useCreateClubPoll(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      body?: string | null;
      allowMultiple: boolean;
      endsAt?: string | null;
      options: string[];
      mentionedProfileIds?: string[];
    }) =>
      ClubApi.createClubPoll({
        organizationId: organizationId!,
        ...input,
      }),
    onSuccess: () => invalidateClubOrg(qc, organizationId),
  });
}

export function useCreateClubEvent(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      description?: string | null;
      startsAt: string;
      endsAt?: string | null;
      allDay?: boolean;
      locationText?: string | null;
      onlineUrl?: string | null;
      maxAttendees?: number | null;
      rsvpDeadline?: string | null;
      series?: EventSeriesInput | null;
      mentionedProfileIds?: string[];
    }) =>
      ClubApi.createClubEvent({
        organizationId: organizationId!,
        ...input,
      }),
    onSuccess: () => invalidateClubOrg(qc, organizationId),
  });
}

export function useCastPollVote(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { pollItemId: string; optionIds: string[] }) =>
      ClubApi.castPollVote(input.pollItemId, input.optionIds),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.pollItemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useCloseClubPoll(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => ClubApi.closeClubPoll(itemId),
    onSuccess: (_d, itemId) => {
      invalidateItem(qc, itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useSetEventRsvp(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { eventItemId: string; status: EventRsvpStatus }) =>
      ClubApi.setEventRsvp(input.eventItemId, input.status),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.eventItemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useSetClubReaction(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      itemId?: string | null;
      commentId?: string | null;
      reactionKey?: string | null;
    }) => ClubApi.setClubReaction(input),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.itemId ?? undefined);
      if (vars.commentId && vars.itemId) {
        void qc.invalidateQueries({
          queryKey: queryKeys.club.comments(vars.itemId),
        });
      }
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useSetClubWatch(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { itemId: string; watching: boolean }) =>
      ClubApi.setClubWatch(input.itemId, input.watching),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useSetClubWatchNotify(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { itemId: string; enabled: boolean }) =>
      ClubApi.setClubWatchNotify(input.itemId, input.enabled),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.itemId);
    },
  });
}

export function useAddClubComment(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      itemId: string;
      body: string;
      mentionedProfileIds?: string[];
    }) => ClubApi.addClubComment(input),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useEditClubComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      commentId: string;
      body: string;
      itemId: string;
      mentionedProfileIds?: string[];
    }) =>
      ClubApi.editClubComment({
        commentId: input.commentId,
        body: input.body,
        mentionedProfileIds: input.mentionedProfileIds,
      }),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.itemId);
    },
  });
}

export function useWithdrawClubComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { commentId: string; itemId: string }) =>
      ClubApi.withdrawClubComment(input.commentId),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.itemId);
    },
  });
}

export function useWithdrawClubItem(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => ClubApi.withdrawClubItem(itemId),
    onSuccess: (_d, itemId) => {
      invalidateItem(qc, itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useCancelClubEvent(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { itemId: string; reason: string }) =>
      ClubApi.cancelClubEvent(input.itemId, input.reason),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useSetClubPostOfficial(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { itemId: string; isOfficial: boolean }) =>
      ClubApi.setClubPostOfficial(input.itemId, input.isOfficial),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useSetClubPostPinned(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { itemId: string; pinned: boolean }) =>
      ClubApi.setClubPostPinned(input.itemId, input.pinned),
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useUpsertEventHelperRole(organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ClubApi.upsertEventHelperRole,
    onSuccess: (_d, vars) => {
      invalidateItem(qc, vars.eventItemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useDeleteEventHelperRole(itemId?: string, organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => ClubApi.deleteEventHelperRole(roleId),
    onSuccess: () => {
      invalidateItem(qc, itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useJoinHelperRole(itemId?: string, organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => ClubApi.joinHelperRole(roleId),
    onSuccess: () => {
      invalidateItem(qc, itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useLeaveHelperRole(itemId?: string, organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => ClubApi.leaveHelperRole(roleId),
    onSuccess: () => {
      invalidateItem(qc, itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useAssignHelper(itemId?: string, organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { roleId: string; profileId: string }) =>
      ClubApi.assignHelper(input.roleId, input.profileId),
    onSuccess: () => {
      invalidateItem(qc, itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}

export function useUnassignHelper(itemId?: string, organizationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { roleId: string; profileId: string }) =>
      ClubApi.unassignHelper(input.roleId, input.profileId),
    onSuccess: () => {
      invalidateItem(qc, itemId);
      invalidateClubOrg(qc, organizationId);
    },
  });
}
